/**
 * P2P DataChannel connection for lockstep netplay.
 *
 * Sets up a WebRTC PeerConnection with an unreliable unordered DataChannel
 * for redundant input exchange and an unreliable channel for latency probes.
 * No media streams — both players render locally.
 *
 * Signaling uses the existing network.sendSignal / network.onSignal path
 * (WebSocket → server relay → WebSocket).
 */

const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

const DC_PING = 0x01;
const DC_PONG = 0x02;
const DC_PING_SIZE = 9;
export const DC_PING_INTERVAL_MS = 500;

// Message types on the reliable channel
const MSG_INPUT = 0x10;
const MSG_SYNC  = 0x11;
const MSG_INPUT_BATCH = 0x12;
const INPUT_REDUNDANCY_FRAMES = 12;
export const CHANNEL_BUFFERED_AMOUNT_LIMIT = 256 * 1024;
export const NETPLAY_DEBUG_STORAGE_KEY = 'retroplay-netplay-debug';
const NETPLAY_DEBUG_QUERY_KEYS = ['netLag', 'netJitter', 'netLoss'];
const NETPLAY_DEBUG_MAX_LAG_MS = 5000;
const NETPLAY_DEBUG_MAX_JITTER_MS = 5000;

const DISABLED_DEBUG_PROFILE = Object.freeze({
  enabled: false,
  lagMs: 0,
  jitterMs: 0,
  lossPct: 0,
});

function clampNumber(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function normalizeDebugProfile(profile = {}) {
  const lagMs = clampNumber(Number(profile.lagMs ?? profile.lag ?? 0), 0, NETPLAY_DEBUG_MAX_LAG_MS);
  const jitterMs = clampNumber(Number(profile.jitterMs ?? profile.jitter ?? 0), 0, NETPLAY_DEBUG_MAX_JITTER_MS);
  const lossPct = clampNumber(Number(profile.lossPct ?? profile.loss ?? 0), 0, 100);
  return {
    enabled: lagMs > 0 || jitterMs > 0 || lossPct > 0,
    lagMs,
    jitterMs,
    lossPct,
  };
}

function safeStorage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

function readStoredDebugProfile(storage = safeStorage()) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(NETPLAY_DEBUG_STORAGE_KEY);
    if (!raw) return null;
    return normalizeDebugProfile(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeStoredDebugProfile(profile, storage = safeStorage()) {
  if (!storage) return;
  try {
    if (!profile.enabled) {
      storage.removeItem(NETPLAY_DEBUG_STORAGE_KEY);
    } else {
      storage.setItem(NETPLAY_DEBUG_STORAGE_KEY, JSON.stringify(profile));
    }
  } catch {
    // Storage can be blocked in private contexts; the active URL profile still works.
  }
}

function readQueryDebugProfile(locationObj = globalThis.location) {
  if (!locationObj?.search) return null;
  const params = new URLSearchParams(locationObj.search);
  const hasDebugParam = NETPLAY_DEBUG_QUERY_KEYS.some((key) => params.has(key))
    || params.has('netDebug');
  if (!hasDebugParam) return null;

  const explicitMode = `${params.get('netDebug') || ''}`.toLowerCase();
  if (explicitMode === 'off' || explicitMode === 'false' || explicitMode === '0') {
    return { ...DISABLED_DEBUG_PROFILE };
  }

  return normalizeDebugProfile({
    lagMs: params.get('netLag') ?? 0,
    jitterMs: params.get('netJitter') ?? 0,
    lossPct: params.get('netLoss') ?? 0,
  });
}

export function getLockstepDebugSearch(locationObj = globalThis.location) {
  if (!locationObj?.search) return '';
  const params = new URLSearchParams(locationObj.search);
  const out = new URLSearchParams();
  for (const key of [...NETPLAY_DEBUG_QUERY_KEYS, 'netDebug']) {
    if (params.has(key)) out.set(key, params.get(key) ?? '');
  }
  const search = out.toString();
  return search ? `?${search}` : '';
}

export function loadLockstepDebugProfile({
  locationObj = globalThis.location,
  storage = safeStorage(),
} = {}) {
  const queryProfile = readQueryDebugProfile(locationObj);
  if (queryProfile) {
    writeStoredDebugProfile(queryProfile, storage);
    return queryProfile;
  }
  return readStoredDebugProfile(storage) || { ...DISABLED_DEBUG_PROFILE };
}

export class LockstepConnection {
  /**
   * @param {import('./network.js').Network} network
   * @param {boolean} isInitiator  true = creates offer (player 1)
   * @param {{ debugProfile?: object }} [opts]
   */
  constructor(network, isInitiator, opts = {}) {
    this.network = network;
    this.isInitiator = isInitiator;
    this.pc = null;
    this.inputChannel = null;
    this.probeChannel = null;

    this.onRemoteInput = null;
    this.onRemoteSyncHash = null;
    this.onConnected = null;
    this.onDisconnected = null;
    this.onP2PRtt = null;

    this.p2pLatency = null;
    this._dcPingInterval = null;
    this._pendingSignals = [];
    this._ready = false;
    this._recentInputs = new Map();
    this._debugProfile = normalizeDebugProfile(opts.debugProfile ?? loadLockstepDebugProfile());
    this._debugTimers = new Set();
    this._destroyed = false;

    if (this._debugProfile.enabled) {
      console.info(
        `[lockstep-netdebug] lag=${this._debugProfile.lagMs}ms `
        + `jitter=${this._debugProfile.jitterMs}ms loss=${this._debugProfile.lossPct}%`
      );
    }

    this._prevOnSignal = network.onSignal;
    network.onSignal = (payload) => {
      if (this._ready) {
        this._handleSignal(payload);
      } else {
        this._pendingSignals.push(payload);
      }
    };
  }

  async start() {
    this.pc = new RTCPeerConnection(RTC_CONFIG);

    this.pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.network.sendSignal({ type: 'ice', candidate: e.candidate });
      }
    };

    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState;
      if ((state === 'disconnected' || state === 'failed') && this.onDisconnected) {
        this.onDisconnected();
      }
    };

    if (this.isInitiator) {
      // Create both DataChannels
      this.inputChannel = this.pc.createDataChannel('lockstep-input', {
        ordered: false,
        maxRetransmits: 0,
      });
      this.inputChannel.binaryType = 'arraybuffer';
      this.inputChannel.onmessage = (e) => this._handleInputMessage(e);
      this.inputChannel.onopen = () => this._onChannelsReady();

      this.probeChannel = this.pc.createDataChannel('lockstep-probe', {
        ordered: false,
        maxRetransmits: 0,
      });
      this.probeChannel.binaryType = 'arraybuffer';
      this.probeChannel.onmessage = (e) => this._handleProbeMessage(e);

      // Flush pending signals and create offer
      this._flushPending();
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      this.network.sendSignal({ type: 'offer', sdp: offer.sdp });
    } else {
      // Responder: channels arrive via ondatachannel
      this.pc.ondatachannel = (e) => {
        const ch = e.channel;
        ch.binaryType = 'arraybuffer';
        if (ch.label === 'lockstep-input') {
          this.inputChannel = ch;
          ch.onmessage = (ev) => this._handleInputMessage(ev);
          ch.onopen = () => this._onChannelsReady();
        } else if (ch.label === 'lockstep-probe') {
          this.probeChannel = ch;
          ch.onmessage = (ev) => this._handleProbeMessage(ev);
        }
      };
      this._flushPending();
    }
  }

  _flushPending() {
    this._ready = true;
    for (const sig of this._pendingSignals) {
      this._handleSignal(sig);
    }
    this._pendingSignals = [];
  }

  async _handleSignal(payload) {
    if (!this.pc) return;
    try {
      if (payload.type === 'offer') {
        await this.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.network.sendSignal({ type: 'answer', sdp: answer.sdp });
      } else if (payload.type === 'answer') {
        await this.pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
      } else if (payload.type === 'ice') {
        await this.pc.addIceCandidate(payload.candidate);
      }
    } catch (e) {
      console.warn('[lockstep-conn] Signal error:', e);
    }
  }

  _onChannelsReady() {
    this._startPingLoop();
    if (this.onConnected) this.onConnected();
  }

  /**
   * Send local input for a frame, redundantly including recent inputs.
   * Binary batch:
   *   [MSG_INPUT_BATCH(1)] [count(1)] repeated count times:
   *   [frame(4, BE)] [bitmask(2, BE)]
   *
   * The receiver still accepts legacy single-input packets for compatibility:
   *   [MSG_INPUT(1)] [frame(4, BE)] [bitmask(2, BE)]
   */
  sendInput(frame, bitmask) {
    if (!this.inputChannel || this.inputChannel.readyState !== 'open') return;
    if (!Number.isInteger(frame)) return;
    this._rememberInput(frame, bitmask);
    const entries = [...this._recentInputs.entries()].sort((a, b) => a[0] - b[0]);
    const buf = new ArrayBuffer(2 + entries.length * 6);
    const view = new DataView(buf);
    view.setUint8(0, MSG_INPUT_BATCH);
    view.setUint8(1, entries.length);
    let offset = 2;
    for (const [f, mask] of entries) {
      view.setUint32(offset, f, false);
      view.setUint16(offset + 4, mask, false);
      offset += 6;
    }
    this._sendInputPayload(buf);
  }

  _rememberInput(frame, bitmask) {
    if (!Number.isInteger(frame)) return;
    this._recentInputs.set(frame, bitmask & 0xFFFF);
    const cutoff = frame - INPUT_REDUNDANCY_FRAMES + 1;
    for (const f of this._recentInputs.keys()) {
      if (f < cutoff) this._recentInputs.delete(f);
    }
  }

  /**
   * Send a desync-detection hash for a frame.
   * Binary: [MSG_SYNC(1)] [frame(4, BE)] [hash(string, UTF-8)]
   */
  sendSyncHash(frame, hash) {
    if (!this.inputChannel || this.inputChannel.readyState !== 'open') return;
    const hashBytes = new TextEncoder().encode(hash);
    const buf = new ArrayBuffer(5 + hashBytes.length);
    const view = new DataView(buf);
    view.setUint8(0, MSG_SYNC);
    view.setUint32(1, frame, false);
    new Uint8Array(buf, 5).set(hashBytes);
    this._sendInputPayload(buf);
  }

  clearRecentInputs() {
    this._recentInputs.clear();
  }

  _handleInputMessage(e) {
    if (!(e.data instanceof ArrayBuffer)) return;
    this._receiveDebugPacket(() => this._parseInputMessage(e.data));
  }

  _parseInputMessage(data) {
    if (!(data instanceof ArrayBuffer)) return;
    const view = new DataView(data);
    const type = view.getUint8(0);

    if (type === MSG_INPUT && data.byteLength >= 7) {
      const frame = view.getUint32(1, false);
      const bitmask = view.getUint16(5, false);
      if (this.onRemoteInput) this.onRemoteInput(frame, bitmask);
    } else if (type === MSG_INPUT_BATCH && data.byteLength >= 2) {
      const count = view.getUint8(1);
      const expectedBytes = 2 + count * 6;
      if (data.byteLength < expectedBytes) return;
      let offset = 2;
      for (let i = 0; i < count; i++) {
        const frame = view.getUint32(offset, false);
        const bitmask = view.getUint16(offset + 4, false);
        if (this.onRemoteInput) this.onRemoteInput(frame, bitmask);
        offset += 6;
      }
    } else if (type === MSG_SYNC && data.byteLength > 5) {
      const frame = view.getUint32(1, false);
      const hash = new TextDecoder().decode(new Uint8Array(data, 5));
      if (this.onRemoteSyncHash) this.onRemoteSyncHash(frame, hash);
    }
  }

  // ── P2P Latency Probes ──────────────────────────────────

  _startPingLoop() {
    this._stopPingLoop();
    this._sendPing();
    this._dcPingInterval = setInterval(() => this._sendPing(), DC_PING_INTERVAL_MS);
  }

  _stopPingLoop() {
    if (this._dcPingInterval) {
      clearInterval(this._dcPingInterval);
      this._dcPingInterval = null;
    }
  }

  _sendPing() {
    if (!this.probeChannel || this.probeChannel.readyState !== 'open') return;
    const buf = new ArrayBuffer(DC_PING_SIZE);
    const view = new DataView(buf);
    view.setUint8(0, DC_PING);
    view.setFloat64(1, performance.now());
    this._sendProbePayload(buf);
  }

  _handleProbeMessage(e) {
    if (!(e.data instanceof ArrayBuffer)) return;
    this._receiveDebugPacket(() => this._parseProbeMessage(e.data));
  }

  _parseProbeMessage(data) {
    if (!(data instanceof ArrayBuffer) || data.byteLength !== DC_PING_SIZE) return;
    const view = new DataView(data);
    const tag = view.getUint8(0);

    if (tag === DC_PING) {
      // Echo back as pong
      const buf = new ArrayBuffer(DC_PING_SIZE);
      const pongView = new DataView(buf);
      pongView.setUint8(0, DC_PONG);
      pongView.setFloat64(1, view.getFloat64(1));
      this._sendProbePayload(buf);
    } else if (tag === DC_PONG) {
      const sent = view.getFloat64(1);
      this.p2pLatency = Math.round(performance.now() - sent);
      if (this.onP2PRtt) this.onP2PRtt(this.p2pLatency);
    }
  }

  _sendInputPayload(buf) {
    this._sendDebugPacket(this.inputChannel, buf);
  }

  _sendProbePayload(buf) {
    this._sendDebugPacket(this.probeChannel, buf);
  }

  _sendDebugPacket(channel, buf) {
    if (!channel || channel.readyState !== 'open') return;
    if (this._isChannelBackedUp(channel)) return;
    if (this._debugShouldDropPacket()) return;
    this._debugDelay(() => {
      if (!this._destroyed && channel.readyState === 'open' && !this._isChannelBackedUp(channel)) {
        channel.send(buf);
      }
    });
  }

  _isChannelBackedUp(channel) {
    return Number.isFinite(channel?.bufferedAmount)
      && channel.bufferedAmount > CHANNEL_BUFFERED_AMOUNT_LIMIT;
  }

  _receiveDebugPacket(fn) {
    if (this._debugShouldDropPacket()) return;
    this._debugDelay(fn);
  }

  _debugShouldDropPacket() {
    return this._debugProfile.enabled
      && this._debugProfile.lossPct > 0
      && Math.random() * 100 < this._debugProfile.lossPct;
  }

  _debugDelay(fn) {
    const delayMs = this._debugDelayMs();
    if (delayMs <= 0) {
      fn();
      return;
    }
    const timer = setTimeout(() => {
      this._debugTimers.delete(timer);
      if (!this._destroyed) fn();
    }, delayMs);
    this._debugTimers.add(timer);
  }

  _debugDelayMs() {
    if (!this._debugProfile.enabled) return 0;
    const jitter = this._debugProfile.jitterMs > 0
      ? (Math.random() * 2 - 1) * this._debugProfile.jitterMs
      : 0;
    return Math.max(0, Math.round(this._debugProfile.lagMs + jitter));
  }

  /** True when the input channel is open and ready. */
  get isOpen() {
    return this.inputChannel?.readyState === 'open';
  }

  destroy() {
    this._destroyed = true;
    this._stopPingLoop();
    for (const timer of this._debugTimers) clearTimeout(timer);
    this._debugTimers.clear();
    this.network.onSignal = this._prevOnSignal;
    if (this.inputChannel) {
      this.inputChannel.close?.();
      this.inputChannel = null;
    }
    if (this.probeChannel) {
      this.probeChannel.close?.();
      this.probeChannel = null;
    }
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }
    this.onRemoteInput = null;
    this.onRemoteSyncHash = null;
    this.onConnected = null;
    this.onDisconnected = null;
    this.onP2PRtt = null;
  }
}
